import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import {  useDevices, usePlant, useTcpClients, useUserDetail, useUserPermissions } from "../../services/hooks";
import { useDeviceContext } from "../../context/socketContext";
import { Button, DetailCard, Dialog, Dropdown, tCell } from "../utils";
import { buttonClasses } from "../utils/theme";
import "./DeviceMap.css";
import { GenericTableInfinite } from "../utils/GenericTable";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import dayjs from "dayjs";
import { capitalizeCamelCase } from "../../services/functions";
export default function DeviceMap({}) {
	/**
	 * Plants have checkpoints
	 * Checkpoints have devices
	 * This map is to show all locations of the devices with their status (online/offline)
	 * Current active devices will come from TcpClients
	 * Socket update of device going offline/online will come from deviceData in useDeviceContext
	 *
	 * We'll first get all checkpoints from useCheckpoints - this will get us all checkpoints that user has access to
	 * From each checkpoint we will get the latitude and longitude and device id
	 * Using the lat and long we will plot the geopoint on the map
	 */
	const navigate = useNavigate();
	const { userPermissions, isFetched } = useUserPermissions();
	const {  isSuperAdmin } = useUserDetail();
	const [selectedPlantIds, setSelectedPlantIds] = useState([]);
	const mapRef = useRef(null);
	const [isMapLoaded, setIsMapLoaded] = useState(true);
	const [mapInstance, setMapInstance] = useState(null);
	const [showTable, setShowTable] = useState(false);
	const [filteredDeviceData, setFilteredDeviceData] = useState([]);
	const [filter, setFilter] = useState({});
	const { tcpClients, getTcpClients } = useTcpClients({pagesize: 1000});
	const { deviceData } = useDeviceContext();
	const { plantOptions, plants } = usePlant({}, "deviceMapPlants");
	const { devices, isLoading, getDevices } = useDevices({});
	const [isOpen, setIsOpen] = useState(false);
    const markerClusterRef = useRef(null);
	// ── Persistent map + overlays so we never rebuild the whole map on socket ticks ──
	const mapObjRef = useRef(null);                                  // the single google.maps.Map
	const markerLibRef = useRef(null);                               // { AdvancedMarkerElement, PinElement }
	const overlaysRef = useRef({ markers: new Map(), polygons: new Map() }); // keyed by plant._id
	const redrawTimerRef = useRef(null);                             // debounce handle
	const didFitRef = useRef(false);                                 // fitBounds only once
	const lastFitSelectionRef = useRef(null);                        // refit when selected plants change

	// ── Stable copy of ALL devices — only updated on mount and explicit resets ──
	const [allDevices, setAllDevices] = useState([]);
	const allDevicesFetchedRef = useRef(false);

	// Populate allDevices once on mount (full, unfiltered fetch)
	useEffect(() => {
		if (allDevicesFetchedRef.current) return;
		allDevicesFetchedRef.current = true;
		(async () => {
			try {
				const res = await getDevices({});
				setAllDevices(res?.data?.data || []);
			} catch (err) {
				console.error("Error fetching all devices:", err);
			}
		})();
	}, []);

	// ── Summary popup state ──
	const [summaryPopup, setSummaryPopup] = useState({ open: false, type: "", warehouses: [] });
	// const getLastActiveCell = (device, deviceInfo) => {
	// 	if (device.deviceType === "speaker") return "-";
	// 	if (deviceInfo) return tCell("-");
	// 	if (!device.lastActive) return tCell("-");
	// 	return tCell(dayjs(device.lastActive).format("DD MMM YYYY, hh:mm A"));
	// };
	const getLastActiveCell = (device, deviceInfo) => {
		if (device.deviceType === "speaker") return tCell("-");

		const isConnected = deviceInfo?.deviceStatus === true || deviceInfo?.status === "Active";

		if (isConnected) return tCell("-");

		const lastActive = deviceInfo?.lastActive || device.lastActive;
		if (!lastActive) return tCell("-");
		return tCell(dayjs(lastActive).format("DD MMM YYYY, hh:mm A"));
	};

    const getDeviceTypeLabel = (device) => {
        const type = capitalizeCamelCase(device?.deviceType);
        const subType = device?.deviceSubType ? ` (${device?.deviceSubType})` : "";
        return `${type}${subType}`;
    };
	const isDeviceOnline = (device) => {
		const deviceInfo = deviceData[device.imei] !== undefined ? deviceData[device.imei] : tcpClients[device.imei];
		return deviceInfo?.deviceStatus === true || deviceInfo?.status === "Active";
	};
	// ── Warehouse status summary — uses allDevices so counts never change on filter ──
	// Also reacts to deviceData (socket) and tcpClients for live updates
	const warehouseSummary = useMemo(() => {
		const online = [];
		const offline = [];
		const partial = [];

		plants.forEach((plant) => {
			const plantDevices = allDevices.filter((d) => d.plantId === plant._id);
			const total = plantDevices.length;
			let onlineCount = 0;

			plantDevices.forEach((d) => {
				if (isDeviceOnline(d)) onlineCount++;
			});

			const entry = { ...plant, totalDevices: total, onlineDevices: onlineCount };

			if (total === 0) {
				offline.push(entry);
			} else if (onlineCount === total) {
				online.push(entry);
			} else if (onlineCount === 0) {
				offline.push(entry);
			} else {
				partial.push(entry);
			}
		});

		return { online, offline, partial };
	}, [plants, allDevices, deviceData, tcpClients]);

	// ── Summary card click ──
	const handleSummaryClick = (type) => {
		const warehouses = warehouseSummary[type] || [];
		setSummaryPopup({ open: true, type, warehouses });
	};

	const handleSummaryClose = () => {
		setSummaryPopup({ open: false, type: "", warehouses: [] });
	};

	// ── Warehouse row click inside summary popup ──
	const handleWarehouseClick = async (plant) => {
		handleSummaryClose();
		try {
			const res = await getDevices({ plantIds: [plant._id] });
			setFilteredDeviceData(res?.data?.data || []);
			setShowTable(true);
			setIsOpen(true);
			setFilter((o) => ({ ...o, plantIds: [plant._id] }));
			setSelectedPlantIds([plant._id]);

			if (mapInstance && plant.factoryCoordinates?.length) {
				const centroid = getCentroid(plant.factoryCoordinates);
				if (centroid) {
					mapInstance.panTo(centroid);
					mapInstance.setZoom(16);
				}
			}
		} catch (err) {
			console.error("Error fetching plant devices:", err);
			setFilteredDeviceData([]);
			setShowTable(true);
		}
	};

	// ── Popup table rows via GenericTableInfinite (no # column) ──
	const showDeviceCount = summaryPopup.type === "partial";
	const summaryPopupHeader = showDeviceCount
		? ["Warehouse Name", "Devices (Online / Total)"]
		: ["Warehouse Name"];
	const summaryPopupRows = useMemo(() => {
		if (!summaryPopup.open) return [];
		const isPartial = summaryPopup.type === "partial";
		return summaryPopup.warehouses.map((w) => {
			const row = [
				tCell(
					<span
						className="summary-warehouse-link"
						onClick={() => handleWarehouseClick(w)}
						title="Click to view devices"
					>
						{w.plantName || "-"}
					</span>
				),
			];
			if (isPartial) {
				row.push(tCell(`${w.onlineDevices} / ${w.totalDevices}`));
			}
			return row;
		});
	}, [summaryPopup]);

	let header = ["Device Name", "Status", "Warehouse", "IMEI", "Device Type", "Last Active"];
	let rows = [];

	if (showTable) {
		rows = filteredDeviceData.map((d) => {
			let d_info = deviceData[d.imei] !== undefined ? deviceData[d.imei] : tcpClients[d.imei];
			return [
				tCell(d?.Displayname || d?.displayName || "-"),
				tCell(<span className={`deviceStatus ${d_info ? "connected" : ""}`}></span>),
				tCell(d?.plantName || "-"),
				tCell(d?.imei || "-"),
				tCell(getDeviceTypeLabel(d)),
				getLastActiveCell(d, d_info),
			];
		});
	} else {
		rows = devices.map((d) => {
			let d_info = deviceData[d.imei] !== undefined ? deviceData[d.imei] : tcpClients[d.imei];
			return [
				tCell(d?.Displayname || d?.displayName || "-"),
				tCell(<span className={`deviceStatus ${d_info ? "connected" : ""}`}></span>),
				tCell(d?.imei || "-"),
			];
		});
	}

	const handleDetailClose = () => {
		setIsOpen(false);
		handleFilterClear();
		setShowTable(false);
	};

	const handleFilterChange = (name, value) => {
		setFilter((o) => ({ ...o, [name]: value }));
		setSelectedPlantIds(value);
		getDevices({})
		getTcpClients({pagesize: 1000})
	};

	const handleFilterClear =() => {
		setFilter({});
		setSelectedPlantIds([]);
		setFilteredDeviceData([]); 
		setShowTable(false);
		getDevices({}).then((res) => {
			// Re-sync allDevices on reset so summary stays accurate
			setAllDevices(res?.data?.data || []);
		});
		getTcpClients({pagesize: 1000})
	};

	const handleFilterSubmit = async () => {
		try {
			let res = null;
			if (filter.plantIds?.length) {
				res = await getDevices({ plantIds: filter.plantIds });
			} else {
				// if no plantIds, fetching all by passing []
				res = await getDevices({ plantIds: [] });
            }
			setFilteredDeviceData(res?.data?.data || []);
			setShowTable(true);
			renderOverlays();
		} catch (err) {
			console.error("Error fetching devices for filter:", err);
			setFilteredDeviceData([]);
			setShowTable(true);
		}
	};

    const getCentroid = (coords) => {
        if (!coords || coords.length === 0) return null;
        const total = coords.length;
        const sum = coords.reduce((acc, point) => {
            acc.lat += point.lat;
            acc.lng += point.lng;
            return acc;
        }, { lat: 0, lng: 0 });
        return {
            lat: sum.lat / total,
            lng: sum.lng / total
        };
    };

    const getPlantDevices = async (plantId) => {
		try {
			const res = await getDevices({ plantIds: [plantId] });
			setFilteredDeviceData(res?.data?.data || []);
			setShowTable(true);
			setIsOpen(true);
		} catch (err) {
			console.error("Error fetching plant devices:", err);
			setFilteredDeviceData([]);
			setShowTable(true);
		}
    };
	// ── Create the google.maps.Map exactly once; reused for the page's lifetime ──
	async function ensureMap() {
		if (mapObjRef.current) return mapObjRef.current;
		const lib = await google.maps.importLibrary("marker");
		markerLibRef.current = { AdvancedMarkerElement: lib.AdvancedMarkerElement, PinElement: lib.PinElement };
		const map = new google.maps.Map(mapRef.current, {
			center: { lat: 20.5937, lng: 78.9629 },
			zoom: 4.5,
			mapId: "deviceMap",
		});
		mapObjRef.current = map;
		setMapInstance(map);
		return map;
	}

	// ── Compute marker color + status for a plant from live device/tcp state ──
	function computePlantStatus(plant) {
		const devicesForMap = allDevices.length > 0 ? allDevices : devices;
		const plantDevices = devicesForMap.filter((d) => d.plantId === plant._id);
		const total = plantDevices.length;
		let onlineCount = 0;
		plantDevices.forEach((d) => {
			if (isDeviceOnline(d)) onlineCount++;
		});
		let bg;
		if (total > 0) bg = onlineCount === total ? "#28a745" : onlineCount === 0 ? "red" : "#ff7700";
		else bg = "red";
		const status = total > 0 && onlineCount === total ? "online" : onlineCount === 0 ? "offline" : "partial";
		return { bg, status };
	}

	// ── Update overlays on the EXISTING map: reuse markers/polygons, only recolor on change ──
	async function renderOverlays() {
		try {
			const map = await ensureMap();
			const { AdvancedMarkerElement, PinElement } = markerLibRef.current;
			const store = overlaysRef.current;

			const visiblePlants = selectedPlantIds.length > 0
				? plants.filter((plant) => selectedPlantIds.includes(plant._id))
				: plants;
			const visibleIds = new Set(visiblePlants.map((p) => p._id));

			let membershipChanged = false;
			let statusChanged = false;

			// Drop overlays for plants no longer visible
			for (const [pid, marker] of store.markers) {
				if (!visibleIds.has(pid)) { marker.map = null; store.markers.delete(pid); membershipChanged = true; }
			}
			for (const [pid, poly] of store.polygons) {
				if (!visibleIds.has(pid)) { poly.setMap(null); store.polygons.delete(pid); }
			}

			const bounds = new google.maps.LatLngBounds();

			visiblePlants.forEach((plant) => {
				const coords = plant.factoryCoordinates;
				if (!coords || coords.length === 0) return;
				const centroid = getCentroid(coords);
				if (!centroid) return;

				bounds.extend(new google.maps.LatLng(centroid.lat, centroid.lng));

				const { bg, status } = computePlantStatus(plant);

				// Polygon — create once, never rebuilt
				if (!store.polygons.has(plant._id)) {
					const polygon = new google.maps.Polygon({
						paths: coords.map((c) => ({ lat: c.lat, lng: c.lng })),
						strokeColor: "#a5624eff",
						strokeOpacity: 0.8,
						strokeWeight: 2,
						fillColor: "#a5624eff",
						fillOpacity: 0.35,
					});
					polygon.setMap(map);
					store.polygons.set(plant._id, polygon);
				}

				// Marker — create once; on later ticks only swap the pin when color changed
				let marker = store.markers.get(plant._id);
				if (!marker) {
					const pin = new PinElement({ scale: 1, background: bg, borderColor: bg, glyphColor: "white" });
					marker = new AdvancedMarkerElement({
						map,
						position: { lat: centroid.lat, lng: centroid.lng },
						title: plant.plantName,
						content: pin.element,
					});
					marker.addListener("click", () => {
						map.panTo({ lat: centroid.lat, lng: centroid.lng });
						map.setZoom(16);
						getPlantDevices(plant._id);
						setFilter((o) => ({ ...o, plantIds: [plant._id] }));
						setSelectedPlantIds([plant._id]);
					});
					marker._bg = bg;
					store.markers.set(plant._id, marker);
					membershipChanged = true;
				} else if (marker._bg !== bg) {
					// recolor in place — single small DOM swap instead of full marker rebuild
					const pin = new PinElement({ scale: 1, background: bg, borderColor: bg, glyphColor: "white" });
					marker.content = pin.element;
					marker._bg = bg;
				}
				if (marker.customStatus !== status) statusChanged = true;
				marker.customStatus = status;
			});

			const selectionKey = selectedPlantIds.join(",");
			const shouldFitBounds = !didFitRef.current || lastFitSelectionRef.current !== selectionKey;

			// Fit on initial load and selection changes, but not on socket-only redraws
			if (shouldFitBounds && !bounds.isEmpty()) {
				map.fitBounds(bounds);
				google.maps.event.addListenerOnce(map, "bounds_changed", () => {
					if (map.getZoom() > 16) map.setZoom(16);
				});
				didFitRef.current = true;
				lastFitSelectionRef.current = selectionKey;
			}

			// Clusterer — resync on marker set changes or status changes that affect cluster color
			if (membershipChanged || statusChanged || !markerClusterRef.current) {
				const markerList = Array.from(store.markers.values());
				if (markerClusterRef.current) {
					markerClusterRef.current.clearMarkers(!statusChanged);
					markerClusterRef.current.addMarkers(markerList);
				} else {
					markerClusterRef.current = new MarkerClusterer({
						map,
						markers: markerList,
						renderer: {
							render: ({ count, markers, position }) => {
								const hasOnline = markers.some((m) => m.customStatus === "online" || m.customStatus === "partial");
								const hasOffline = markers.some((m) => m.customStatus === "offline" || m.customStatus === "partial");
								const backgroundColor = hasOnline && !hasOffline ? "#28a745" : !hasOnline && hasOffline ? "red" : "#ff7700";
								return new google.maps.Marker({
									position,
									icon: {
										path: google.maps.SymbolPath.CIRCLE,
										fillColor: backgroundColor,
										fillOpacity: 0.8,
										strokeColor: backgroundColor,
										strokeWeight: 1,
										scale: 15,
									},
									label: { text: String(count), color: "white", fontWeight: "bold" },
								});
							},
						},
					});
				}
			}
		} catch (error) {
			console.error("Error rendering map overlays:", error);
			setIsMapLoaded(false);
		}
	}

	// Debounced redraw — coalesces socket bursts (deviceData/tcpClients) into one update
	useEffect(() => {
		if (!window.google || !mapRef.current || plants.length === 0) return;
		if (redrawTimerRef.current) clearTimeout(redrawTimerRef.current);
		redrawTimerRef.current = setTimeout(() => { renderOverlays(); }, 350);
		return () => { if (redrawTimerRef.current) clearTimeout(redrawTimerRef.current); };
	}, [plants, allDevices, devices, selectedPlantIds, tcpClients, deviceData]);

	useEffect(() => {
		if (selectedPlantIds.length > 0) {
			(async () => {
				try {
					const res = await getDevices({ plantIds: selectedPlantIds });
					setFilteredDeviceData(res?.data?.data || []);
					setShowTable(true);
					setIsOpen(true);
				} catch (err) {
					console.error("Error fetching plant devices:", err);
					setFilteredDeviceData([]);
					setShowTable(true);
				}
			})();
		}
	}, [selectedPlantIds]);

	// IF SOCKET EVENT DOES NOT WORKS PROPERLY.... THEN IN THIS CASE CALLING GETDEVICES AFTER EVERY 90 SECS
	useEffect(() => {
		const fetchTcpClients = async () => {
			await getTcpClients({pagesize: 1000});
		};
		fetchTcpClients();
		// repeat every 90 seconds (90000 ms)
		const interval = setInterval(fetchTcpClients, 90000);
		return () => clearInterval(interval);
	}, []);

	if (isFetched && !isSuperAdmin && userPermissions && Object.keys(userPermissions).length && !userPermissions.viewDeviceMap?.value) return <Navigate to="/not-authorized" replace />;


	const summaryLabel =
		summaryPopup.type === "online"
			? "All Online Warehouses"
			: summaryPopup.type === "offline"
			? "All Offline Warehouses"
			: "Few Online Warehouses";

	return (
		<div className="contentpanel">
			<div className="cust-row flex-algn-cent">
				<div className="cust-col-5">
					<h1 className="title">Device Map</h1>
				</div>
			</div>

			<hr className="bgg" />

			{/* STATS BUTTONS */}
			<div className="device-summary-cards" style={{flexWrap:"wrap"}}>
				<div
					className="summary-card summary-total"
					title="Total warehouses"
				>
					<span className="indicator total"> </span> <span className="summary-label">Total Warehouses : </span><span className="summary-count-total">{plants?.length}</span>
				</div>
				<div
					className="summary-card summary-online"
					onClick={() => handleSummaryClick("online")}
					title="Click to view online warehouses"
				>
					
					<span className="indicator online"> </span> <span className="summary-label">All Online : </span><span className="summary-count">{warehouseSummary.online.length}</span>
				</div>
				<div
					className="summary-card summary-offline"
					onClick={() => handleSummaryClick("offline")}
					title="Click to view offline warehouses"
				>
					<span className="indicator offline"> </span> {" "}
					<span className="summary-label">All Offline : </span> 
					<span className="summary-count">{warehouseSummary.offline.length}</span>
				</div>
				<div
					className="summary-card summary-partial"
					onClick={() => handleSummaryClick("partial")}
					title="Click to view partially online warehouses"
				>
					<span className="indicator fewOffline"> </span> {" "}
					<span className="summary-label">Few Online : </span>
					<span className="summary-count">{warehouseSummary.partial.length}</span>
				</div>
			</div>
			{/* TABLE SUMMARY POPUP */}
			{
				summaryPopup.open && (
					<Dialog handleClose={handleSummaryClose} size="sm" open={true} title={summaryLabel} noBtn>
						<div className="summary-popup-body">
							{summaryPopup.warehouses.length === 0 ? (
								<p className="summary-no-data">No warehouses found.</p>
							) : (
								<GenericTableInfinite
									header={summaryPopupHeader}
									rows={summaryPopupRows}
									isLoading={false}
								/>
							)}
						</div>
					</Dialog>
				)
			}

			<div className="main_content">
				<div style={{ position: "relative" }}>
					<Button
						text="Recenter"
						onClick={() => {
							if (mapInstance) {
								mapInstance.setCenter({ lat: 20.5937, lng: 78.9629 });
								mapInstance.setZoom(4.4);
							}
							// Clearing auto-filled dropdown
							setFilter({});
							setSelectedPlantIds([]);
							getDevices({}).then((res) => {
								setAllDevices(res?.data?.data || []);
							});
							getTcpClients({pagesize: 1000})
							setIsOpen(false)
							setShowTable(false)
						}}
						style={{
							...buttonClasses.lynkitBlackFill,
							position: "absolute",
							top: "1rem",
							right: "9.5rem",
							zIndex: "1",
							width: "max-content",
						}}
					/>
					<Button
						text="Filters"
						onClick={() => {
							setIsOpen(true);
						}}
						style={{
							...buttonClasses.lynkitOrangeFill,
							position: "absolute",
							top: "1rem",
							right: "5rem",
							zIndex: "1",
							width: "max-content",
						}}
					/>
					<div ref={mapRef} style={{ width: "100%", height: "75vh" }} />
				</div>
				<DetailCard isOpen={isOpen} onClose={handleDetailClose} title={"Apply Filter"} height={"69vh"} width={"30vw"}>
					<Dropdown
						name="plantIds"
						label="Warehouse"
						options={plantOptions}
						value={plantOptions.filter((_) => filter.plantIds?.includes(_.value))}
						multiple
						onChange={(opt, name) => {
							handleFilterChange(
								name,
								opt?.map((_) => _.value)
							);
						}}
					/>
					<div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
						<Button text="Reset" onClick={handleFilterClear} style={buttonClasses.lynkitBlackFill} />
						<Button text="Apply Filter" onClick={handleFilterSubmit} style={buttonClasses.lynkitOrangeFill} />
					</div>
					{showTable ? (
						<div style={{ marginTop: "1rem", maxHeight: "52vh", minHeight: "22vh" }}>
							<GenericTableInfinite header={header} rows={rows} isLoading={isLoading} />
						</div>
					) : null}
				</DetailCard>
			</div>
		</div>
	);
}
